import React, { useCallback, useEffect, useState } from 'react';
import { HardDrive, Plus } from 'lucide-react';
import {
  abortEdgeConnectorRotation,
  createEdgeConnector,
  deleteEdgeConnector,
  listEdgeConnectors,
  reissueProvisioningToken,
  revokeEdgeConnector,
  rotateEdgeConnectorSecret,
  setEdgeConnectorBlocked,
  updateExecutionMode,
} from '../../api/edgeConnectors';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmModal from '../../components/ui/ConfirmModal';
import EmptyState from '../../components/ui/EmptyState';
import Input from '../../components/ui/Input';
import { getApiErrorMessage } from '../../utils/errors';
import EdgeConnectorCard from './components/EdgeConnectorCard';
import ExecutionModeCard from './components/ExecutionModeCard';
import LlmCredentialsCard from './components/LlmCredentialsCard';
import OneTimeSecretPanel from './components/OneTimeSecretPanel';

function EdgeConnectorsPage() {
  const { currentMembership } = useAuth();
  // Qualquer membro com acesso ao workspace pode editar o Edge
  const canManage = Boolean(currentMembership);

  const [connectors, setConnectors] = useState([]);
  const [executionMode, setExecutionMode] = useState('standard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingMode, setSavingMode] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState({ id: '', action: '' });
  const [revealed, setRevealed] = useState(null);
  const [pendingRevoke, setPendingRevoke] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listEdgeConnectors();
      setConnectors(response.data || []);
      setExecutionMode(response.meta?.execution_mode || 'standard');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os connectors.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (connector, action, operation, onResult) => {
    setBusy({ id: connector.id, action });
    setError('');
    try {
      const result = await operation();
      if (onResult) {
        onResult(result);
      }
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível concluir a ação.'));
    } finally {
      setBusy({ id: '', action: '' });
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      return;
    }
    setCreating(true);
    setError('');
    try {
      const created = await createEdgeConnector({ name: name.trim() });
      setRevealed({
        kind: 'provisioning',
        value: created.provisioning_token,
        expiresAt: created.provisioning_token_expires_at,
        connectorName: created.name,
      });
      setName('');
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível criar o connector.'));
    } finally {
      setCreating(false);
    }
  };

  const handleModeChange = async (mode) => {
    setSavingMode(true);
    setError('');
    try {
      const account = await updateExecutionMode(mode);
      setExecutionMode(account.execution_mode);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível alterar o modo de execução.'));
    } finally {
      setSavingMode(false);
    }
  };

  const busyActionFor = (connector) =>
    busy.id === connector.id ? busy.action : '';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-content-primary">Edge Connectors</h1>
        <p className="text-content-muted text-sm mt-1">
          Instalações do connector que operam canais e MCPs dentro da infraestrutura do
          cliente. O Core nunca abre conexão para essa rede: é o connector que se conecta.
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {revealed && (
        <OneTimeSecretPanel
          title={
            revealed.kind === 'provisioning'
              ? `Token de provisionamento de ${revealed.connectorName}`
              : `Novo client secret de ${revealed.connectorName}`
          }
          description={
            revealed.kind === 'provisioning'
              ? 'Copie agora e use em `botforge-edge register`. É de uso único e o Core guarda apenas o hash.'
              : 'Copie agora e instale com `botforge-edge install-secret`. O Core guarda apenas o hash.'
          }
          value={revealed.value}
          expiresAt={revealed.expiresAt}
          onDismiss={() => setRevealed(null)}
        />
      )}

      <ExecutionModeCard
        executionMode={executionMode}
        onChange={handleModeChange}
        saving={savingMode}
        canManage={canManage}
      />

      <LlmCredentialsCard canManage={canManage} />

      {canManage && (
        <form onSubmit={handleCreate} className="surface-card p-4 space-y-3">
          <p className="font-medium text-content-primary">Novo connector</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <Input
                id="connector-name"
                name="name"
                label="Nome da instalação"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="cliente-aws-prod"
                hint="Identifica a instalação no painel e nos logs."
              />
            </div>
            <button
              type="submit"
              disabled={creating || !name.trim()}
              className="btn-primary text-sm px-4 py-2.5 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              {creating ? 'Criando...' : 'Criar e gerar token'}
            </button>
          </div>
        </form>
      )}

      {/* O placeholder vale só para a primeira carga. Trocar a lista por ele em
          cada recarga desmontava os cards, e com eles o estado da seção de
          recursos: a confirmação de salvamento sumia e a página voltava ao topo. */}
      {loading && connectors.length === 0 ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando connectors...</p>
        </div>
      ) : connectors.length === 0 ? (
        <EmptyState
          icon={HardDrive}
          title="Nenhum connector cadastrado"
          description="Crie um connector para gerar o token de provisionamento e registrar a instalação do cliente."
        />
      ) : (
        <div className="space-y-3">
          {connectors.map((connector) => (
            <EdgeConnectorCard
              key={connector.id}
              connector={connector}
              canManage={canManage}
              busyAction={busyActionFor(connector)}
              onReissueToken={(item) =>
                runAction(item, 'token', () => reissueProvisioningToken(item.id), (result) =>
                  setRevealed({
                    kind: 'provisioning',
                    value: result.provisioning_token,
                    expiresAt: result.provisioning_token_expires_at,
                    connectorName: result.name,
                  }),
                )
              }
              onRotateSecret={(item) =>
                runAction(item, 'rotate', () => rotateEdgeConnectorSecret(item.id), (result) =>
                  setRevealed({
                    kind: 'secret',
                    value: result.new_client_secret,
                    expiresAt: result.rotation_expires_at,
                    connectorName: result.name,
                  }),
                )
              }
              onAbortRotation={(item) =>
                runAction(item, 'abort', () => abortEdgeConnectorRotation(item.id))
              }
              onToggleBlocked={(item, blocked) =>
                runAction(item, 'block', () => setEdgeConnectorBlocked(item.id, blocked))
              }
              onRevoke={(item) => setPendingRevoke(item)}
              onDelete={(item) =>
                runAction(item, 'delete', () => deleteEdgeConnector(item.id))
              }
              onResourcesSaved={load}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        open={Boolean(pendingRevoke)}
        title="Revogar connector"
        message={
          pendingRevoke
            ? `Revogar "${pendingRevoke.name}" invalida na hora a credencial e todos os tokens já emitidos. A ação não pode ser desfeita: voltar a operar exige criar outro connector e registrar a instalação de novo.`
            : ''
        }
        confirmLabel="Revogar"
        confirmingLabel="Revogando..."
        isConfirming={busy.action === 'revoke'}
        onCancel={() => setPendingRevoke(null)}
        onConfirm={async () => {
          const connector = pendingRevoke;
          setPendingRevoke(null);
          await runAction(connector, 'revoke', () => revokeEdgeConnector(connector.id));
        }}
      />
    </div>
  );
}

export default EdgeConnectorsPage;
